import { isUpcomingBadge, REOPEN_SELLING } from '../../domain/property/formatters';
import { Property, MapFilterChip } from '../../services/api';

// 입주완료 여부 판단 (입주예정일이 지났으면 입주완료)
export const checkMovedIn = (moveInDate: string | null | undefined): boolean => {
  if (!moveInDate) return false;
  const match = moveInDate.match(/^(\d{4})-(\d{2})$/);
  if (!match) return false;
  const moveInYear = parseInt(match[1], 10);
  const moveInMonth = parseInt(match[2], 10);
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  return moveInYear < currentYear || (moveInYear === currentYear && moveInMonth < currentMonth);
};

// 좌표가 대한민국 범위인지 검증 (2026-07-07 TWA 실기 테스트에서 발견된 버그 수정)
// — 해외 GPS/에뮬레이터 기본 위치(미국)로 이동하면 카카오맵 타일이 없는 지역이라
//   화면 전체가 백지가 됨. 한국 밖 좌표면 이동하지 않고 기본(대구) 뷰 유지.
export const isWithinKorea = (lat: number, lng: number): boolean =>
  lat >= 33 && lat <= 39.5 && lng >= 124 && lng <= 132;

// 실제 표시할 상태 반환 (입주완료 자동 판단, 입주예정/마감임박→분양중)
export const getDisplayStatus = (property: Property): string => {
  // 혜택 배너 현장(활성 혜택 보유)은 항상 분양중으로 표시 (입주완료 자동 판단 제외).
  // 대표 지시 2026-09-21: is_special 기준에서 has_active_benefit 기준으로 변경 —
  // 배너가 아닌 일반 마커는 실제 상태(분양예정이면 '분양예정')를 보여준다.
  if (property.has_active_benefit === true) {
    return '분양중';
  }
  // 즉시입주 잔여세대(2026-09-28): 입주일이 지나도 지금 파는 현장 → 분양중으로 보여 기본 필터에 나오게.
  //   판매가 끝난 상태(계약완료·입주완료 등)로 바꿨으면 그 상태를 따른다(코드리뷰).
  if ((property as any).is_reopen === true && REOPEN_SELLING.has(property.status || '')) {
    return '분양중';
  }
  // 입주예정일이 지났으면 입주완료로 표시
  if (checkMovedIn(property.move_in_date)) {
    return '입주완료';
  }
  // 입주예정, 마감임박은 분양중으로 표시
  const status = property.status || '분양중';
  return (status === '입주예정' || status === '마감임박') ? '분양중' : status;
};

/**
 * 마커에 쓰는 글자 — 필터용 상태(getDisplayStatus)와 분리 (2026-09-28 대표).
 * 즉시입주 잔여세대 + '재오픈예정' 은 필터에선 분양중(기본 지도에 나오게), 마커 글자는 '재오픈예정'.
 */
export const getMarkerLabel = (property: Property): string =>
  (property as any).is_reopen === true && property.status === '재오픈예정' ? '재오픈예정' : getDisplayStatus(property);

// 줌 레벨에 따른 마커 스케일 계산
// 줌인(낮은 레벨) = 크게, 줌아웃(높은 레벨) = 작게
export const computeMarkerScale = (zoomLevel: number, isSpecial: boolean): number => {
  if (isSpecial) {
    // 스페셜 마커: 줌아웃 시 더 많이 축소
    if (zoomLevel <= 5) return 1.1;      // 매우 줌인 - 약간 크게
    if (zoomLevel <= 6) return 1.0;      // 줌인 - 기본
    if (zoomLevel <= 7) return 0.85;     // 보통 - 약간 작게
    if (zoomLevel <= 8) return 0.7;      // 약간 줌아웃 - 작게
    if (zoomLevel <= 9) return 0.6;      // 줌아웃 - 많이 작게
    return 0.5;                           // 매우 줌아웃 - 최소 크기
  } else {
    // 일반 마커: 스페셜보다 덜 축소
    if (zoomLevel <= 5) return 1.1;
    if (zoomLevel <= 6) return 1.0;
    if (zoomLevel <= 7) return 0.9;
    if (zoomLevel <= 8) return 0.8;
    if (zoomLevel <= 9) return 0.7;
    return 0.6;
  }
};

export const formatAreaRange = (property: Property) => {
  if (property.min_area && property.max_area) {
    return `${property.min_area}㎡ ~ ${property.max_area}㎡`;
  } else if (property.min_area) {
    return `${property.min_area}㎡부터`;
  }
  return '미정';
};

// 카드 helper
export const buildLoc = (p: Property): string => {
  const addr = p.address ?? '';
  const cleaned = addr.replace(/^대구광역시\s*/, '').replace(/^경상북도\s*/, '').replace(/^경북\s*/, '');
  return cleaned.split(/\s+/).slice(1, 3).join(' ');
};
export const buildImg = (p: Property): string | undefined => {
  const raw = p.image_card_url || p.image_thumbnail_url || p.image_url;
  if (!raw) return undefined;
  if (raw.startsWith('http') || raw.startsWith('/')) return raw;
  return `/media/${raw}`;
};

// 카드 필터링 (admin 관리 chip — 2026-05-19)
// mapFilterApi.list() 로 받은 chip 정의를 sheetCards 에 동적 적용
export const applyChipFilter = (list: Property[], chip: MapFilterChip): Property[] => {
  switch (chip.filter_type) {
    case 'side_inquiry_false':
      return list.filter((p) => !p.is_side_inquiry);
    case 'side_inquiry_true':
      return list.filter((p) => p.is_side_inquiry === true);
    case 'is_pre_registration':
      // '분양예정' 필터 — 배지와 같은 기준 (status=분양예정 또는 is_pre_registration)
      return list.filter((p) => isUpcomingBadge(p.status, (p as any).is_pre_registration === true));
    case 'is_special':
      return list.filter((p) => p.is_special === true);
    case 'min_area_gte': {
      const n = parseFloat(chip.value);
      if (Number.isNaN(n)) return list;
      return list.filter((p) => (p.min_area || 0) >= n || (p.max_area || 0) >= n);
    }
    case 'min_area_lt': {
      const n = parseFloat(chip.value);
      if (Number.isNaN(n)) return list;
      return list.filter((p) => (p.max_area || 0) > 0 && (p.max_area || 0) < n);
    }
    case 'total_units_gte': {
      const n = parseInt(chip.value, 10);
      if (Number.isNaN(n)) return list;
      return list.filter((p) => (p.total_units || 0) >= n);
    }
    case 'status_eq':
      return list.filter((p) => p.status === chip.value);
    case 'status_in': {
      const set = new Set(chip.value.split(',').map((s) => s.trim()).filter(Boolean));
      return list.filter((p) => set.has(p.status));
    }
    case 'business_type_eq':
      return list.filter((p) => p.business_type === chip.value);
    default:
      return list;
  }
};
